/* -----------------------------------------------------------------------
   src/pages/WithdrawSelf.tsx
   – Admin view of all fiat-withdraw requests (with approve / reject)
--------------------------------------------------------------------------- */
import React, { useEffect, useState, useCallback } from "react";
import { CheckCircle, XCircle, RefreshCw } from "lucide-react";
import { format } from "date-fns";

/* ------------------------------------------------------------------ types */
interface WithdrawalRow {
  id: number;
  uid: number;
  amount: number;
  currency: string;

  /* payout details coming from the backend */
  bank_name: string | null;
  account_number: string | null;
  account_holder_name: string | null;
  ifsc_code: string | null;
  upi_id: string | null;

  status: string;          // "In Queue" | "Approved" | "Rejected"
  date: string;            // ISO timestamp
}

/* ------------------------------------------------------------------ page */
export default function WithdrawSelf() {
  const [rows, setRows]           = useState<WithdrawalRow[]>([]);
  const [loading, setLoading]     = useState(false);
  const [actionBusy, setBusyFor]  = useState<number | null>(null);
  const [error, setError]         = useState("");
const [stats, setStats] = useState({
  inQueue: 0,
  approved: 0,
  rejected: 0,
});
  const staffId = localStorage.getItem("currentUserId");

  /* ---------------- fetch list ---------------- */
  const fetchList = useCallback(async () => {
    setError(""); setLoading(true);
    try {
      const res = await fetch("https://api.pandabett.com/getFiatWithdrawData", {
        headers: { "x-staff-id": staffId || "" }
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setRows(data);
        
           setStats({
      inQueue: data
        .filter((r: WithdrawalRow) => r.status === "In Queue")
        .reduce((sum: number, r: WithdrawalRow) => sum + r.amount, 0),
      approved: data
        .filter((r: WithdrawalRow) => r.status === "approved")
        .reduce((sum: number, r: WithdrawalRow) => sum + r.amount, 0),
      rejected: data
        .filter((r: WithdrawalRow) => r.status === "rejected")
        .reduce((sum: number, r: WithdrawalRow) => sum + r.amount, 0),
    });
    } catch (e: any) {
      console.error(e);
      setError(e.message || "Failed to load data");
    } finally { setLoading(false); }
  }, [staffId]);

  useEffect(() => { fetchList(); }, [fetchList]);

  /* ---------------- change status ---------------- */
  const changeStatus = async (id: number, status: "Approved" | "Rejected") => {
    if (actionBusy) return;
    if (!window.confirm(`${status} withdrawal #${id}?`)) return;

    setBusyFor(id);
    try {
      const res = await fetch("https://api.pandabett.com/updateFiatWithdrawStatus", {
        method : "PUT",
        headers: {
          "Content-Type": "application/json",
          "x-staff-id"  : staffId || ""
        },
        body: JSON.stringify({ id, status: status.toLowerCase() })
      });
      if (!res.ok) throw new Error(await res.text());

      /* patch UI in-place */
      // setRows(r => r.map(row => row.id === id ? { ...row, status } : row));
 setRows((prev) => {
      const updatedRows = prev.map((row) =>
        row.id === id ? { ...row, status } : row
      );
      // Update stats based on the new rows
      setStats({
        inQueue: updatedRows
          .filter((r: WithdrawalRow) => r.status === "In Queue")
          .reduce((sum: number, r: WithdrawalRow) => sum + r.amount, 0),
        approved: updatedRows
          .filter((r: WithdrawalRow) => r.status === "Approved")
          .reduce((sum: number, r: WithdrawalRow) => sum + r.amount, 0),
        rejected: updatedRows
          .filter((r: WithdrawalRow) => r.status === "Rejected")
          .reduce((sum: number, r: WithdrawalRow) => sum + r.amount, 0),
      });
      return updatedRows;
    });
    } catch (e: any) {
      console.error(e);
      alert("Failed: " + (e.message || "server error"));
    } finally { setBusyFor(null); }
  };

  /* ---------------- ui ---------------- */
  return (
    <div className="ml-0 lg:ml-[256px] p-4">
      {/* header row */}
      <div className="flex items-center justify-between mb-6">
<div className="mb-6 grid grid-cols-3 gap-4">
  <div className="bg-yellow-50 p-3 rounded">
    <p className="text-sm text-yellow-800">In Queue</p>
    <p className="text-lg font-semibold">{rows[0]?.currency || '₹'} {stats.inQueue.toLocaleString()}</p>
  </div>
  <div className="bg-green-50 p-3 rounded">
    <p className="text-sm text-green-800">Approved</p>
    <p className="text-lg font-semibold">{rows[0]?.currency || '₹'} {stats.approved.toLocaleString()}</p>
  </div>
  <div className="bg-red-50 p-3 rounded">
    <p className="text-sm text-red-800">Rejected</p>
    <p className="text-lg font-semibold">{rows[0]?.currency || '₹'} {stats.rejected.toLocaleString()}</p>
  </div>
</div>
        <button
          onClick={fetchList}
          disabled={loading}
          className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
        >
          <RefreshCw className={loading ? "animate-spin" : ""} size={18}/>
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-red-100 text-red-700 border border-red-300">
          {error}
        </div>
      )}

      {/* table */}
      <div className="overflow-x-auto bg-white shadow rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="whitespace-nowrap">
              <th className="px-4 py-2 text-left font-semibold">ID</th>
              <th className="px-4 py-2 text-left font-semibold">User ID</th>
              <th className="px-4 py-2 text-left font-semibold">Amount</th>
              <th className="px-4 py-2 text-left font-semibold">Method</th>
              <th className="px-4 py-2 text-left font-semibold">Payout Details</th>
              <th className="px-4 py-2 text-left font-semibold">Status</th>
              <th className="px-4 py-2 text-left font-semibold">Requested</th>
              <th className="px-4 py-2 text-left font-semibold">Actions</th>
            </tr>
          </thead>

          <tbody>
            {/* empty state */}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-gray-500">
                No withdrawals
              </td></tr>
            )}

            {/* list rows */}
            {rows.map(r => (
              <tr key={r.id} className="border-t hover:bg-gray-50 whitespace-nowrap">
                <td className="px-4 py-2">{r.id}</td>
                <td className="px-4 py-2">{r.uid}</td>
                <td className="px-4 py-2">
                  {r.currency} {r.amount.toLocaleString()}
                </td>

                {/* method */}
                <td className="px-4 py-2">
                  {r.upi_id
                    ? "UPI"
                    : r.ifsc_code || r.account_number
                    ? "Bank"
                    : "-"}
                </td>

                {/* payout details */}
                <td className="px-4 py-2 text-xs leading-5">
                  {r.upi_id && (
                    <>
                      <span className="font-medium">UPI ID:</span> {r.upi_id}
                    </>
                  )}

                  {!r.upi_id && (
                    <>
                      {r.account_holder_name && (
                        <div><span className="font-medium">Holder:</span> {r.account_holder_name}</div>
                      )}
                      {r.bank_name && (
                        <div><span className="font-medium">Bank:</span> {r.bank_name}</div>
                      )}
                      {r.account_number && (
                        <div><span className="font-medium">A/C No:</span> {r.account_number}</div>
                      )}
                      {r.ifsc_code && (
                        <div><span className="font-medium">IFSC:</span> {r.ifsc_code}</div>
                      )}
                    </>
                  )}
                </td>

                {/* status */}
                <td className="px-4 py-2 font-medium
                  {r.status === 'In Queue'
                    ? 'text-yellow-600'
                    : r.status === 'Approved'
                    ? 'text-green-600'
                    : 'text-red-600'}">
                  {r.status}
                </td>

                {/* date */}
                <td className="px-4 py-2">
                  {format(new Date(r.date), "dd MMM yy HH:mm")}
                </td>

                {/* actions */}
                <td className="px-4 py-2">
                  {r.status === "In Queue" && (
                    <>
                      <button
                        disabled={actionBusy === r.id}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Approve"
                        onClick={() => changeStatus(r.id, "Approved")}
                      >
                        <CheckCircle size={18} className="text-green-600"/>
                      </button>
                      <button
                        disabled={actionBusy === r.id}
                        className="p-1 hover:bg-gray-100 rounded ml-1"
                        title="Reject"
                        onClick={() => changeStatus(r.id, "Rejected")}
                      >
                        <XCircle size={18} className="text-red-600"/>
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}

            {/* loading row */}
            {loading && (
              <tr>
                <td colSpan={8} className="p-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
